import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

// Port of src/app/directives/is-ellipsis.directive.ts ([isEllipsis]).
//
// Directive behavior (mirrored exactly):
// - Truncation test: element.offsetWidth < element.scrollWidth (single line).
// - When truncated, sets element.title = element.textContent (native browser
//   tooltip shows the full text); otherwise removes the title attribute.
// - Re-checks on: MutationObserver (childList + subtree + characterData),
//   'mouseenter' on the element, and window 'resize'. Initial check on mount.
// - Additionally exposes the truncation state as a boolean for React consumers.

export function useIsEllipsis<T extends HTMLElement>(): {
  ref: RefObject<T | null>;
  isEllipsis: boolean;
} {
  const ref = useRef<T>(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkEllipsis = () => {
      const truncated = element.offsetWidth < element.scrollWidth;
      if (truncated) {
        element.title = element.textContent ?? '';
      } else if (element.title) {
        element.removeAttribute('title');
      }
      setIsEllipsis(truncated);
    };

    const mutationObserver = new MutationObserver(checkEllipsis);
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true
    });

    element.addEventListener('mouseenter', checkEllipsis);
    window.addEventListener('resize', checkEllipsis);

    checkEllipsis();

    return () => {
      mutationObserver.disconnect();
      element.removeEventListener('mouseenter', checkEllipsis);
      window.removeEventListener('resize', checkEllipsis);
    };
  }, []);

  return { ref, isEllipsis };
}
